import {
  Box,
  Button,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  Space,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useState } from 'react';

import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { Download, X } from 'tabler-icons-react';
import {
  BusinessFileNames,
  BusinessFileNamesReadable,
} from '../../../enums/business-file-names';
import { BusinessTypes } from '../../../enums/business-type';
import { Roles } from '../../../enums/roles-enum';
import { setFormFiles } from '../../../helpers/merchant-onboarding';
import useAuth from '../../../hooks/useAuth';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { MerchantFileMetadata } from '../../../type/admin.types';
import { ErrorResponse } from '../../../type/error-response.types';
import XInfoTabTitle from '../../x-components/x-info-tab-title.component';
interface Props {
  downloadFiles: () => Promise<void>;
  isRetrievingFiles: boolean;
  files: MerchantFileMetadata[];
  businessTypeId: BusinessTypes;
  merchantId: number;
}

interface ColProps {
  label: string;
  fileType: BusinessFileNames;
}

export interface ViewerProps {
  fileUrl: string | null;
  fileData: {
    fileName: string;
    fileType: string;
  };
}

function MerchantDocuments(props: Props) {
  const {
    downloadFiles,
    isRetrievingFiles,
    files,
    businessTypeId,
    merchantId,
  } = props;
  const { auth } = useAuth();

  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retrievedFile, setRetrievedFile] = useState<ViewerProps>({
    fileUrl: '',
    fileData: {
      fileName: '',
      fileType: '',
    },
  });
  const sortedFiles = setFormFiles(files);
  const axiosPrivate = useAxiosPrivate();

  const retrieveOneFile = async (
    merchantId: number,
    fileName: string,
    fileId: string
  ) => {
    try {
      setLoading(true);
      const response = await axiosPrivate.get(
        `/web/onboard/${merchantId}/files/${fileId}`,
        { responseType: 'blob' }
      );
      const blob: Blob = response.data;
      const url = window.URL.createObjectURL(blob);

      var fileReader = new FileReader();
      fileReader.onloadend = function (e) {
        var arr = new Uint8Array(e.target?.result as ArrayBuffer).subarray(
          0,
          4
        );
        var header = '';
        var fileType = '';

        for (var i = 0; i < arr.length; i++) {
          header += arr[i].toString(16);
        }
        const lowerHeader = header.toLowerCase();

        switch (lowerHeader) {
          case '89504e47':
            fileType = 'png';
            break;
          case 'ffd8ffe0':
          case 'ffd8ffe1':
          case 'ffd8ffe2':
          case 'ffd8ffe3':
          case 'ffd8ffe8':
            fileType = 'jpg';
            break;
          case '255044462d':
          case '25504446':
            fileType = 'pdf';
            break;
          default: // Or you can use the blob.type as fallback
            fileType = 'unknown';
            break;
        }

        setRetrievedFile({
          fileUrl: url,
          fileData: {
            fileName,
            fileType,
          },
        });
      };

      fileReader.readAsArrayBuffer(blob);
      setOpened(true);
    } catch (err: any) {
      let responseObj = await err.response.data.text();

      const error: ErrorResponse = JSON.parse(responseObj);

      showNotification({
        id: 'failed-to-retrieve-single-file',
        message:
          error.error ||
          err.response?.request.statusText ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setLoading(false);
    }
  };

  const ResponsiveCol = (props: ColProps) => {
    const { label, fileType } = props;
    return (
      <Grid.Col sm={6} md={4}>
        <Stack spacing='xs'>
          <Text weight={600} size='sm'>
            {label}
          </Text>
          {sortedFiles[fileType].map((file) => {
            return (
              <div key={file?.id}>
                <UnstyledButton
                  onClick={() => {
                    if (file?.id) {
                      retrieveOneFile(merchantId, file.file.name, file.id);
                    }
                  }}
                >
                  <Text variant='link' size='sm'>
                    {file?.file.name}
                  </Text>
                </UnstyledButton>
              </div>
            );
          })}
        </Stack>
      </Grid.Col>
    );
  };

  const filesToDisplay = () => {
    if (businessTypeId === BusinessTypes.STARTER) {
      return (
        <>
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY_BACK}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY_BACK}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY_SIGNATURE}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY_SIGNATURE}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.BARANGAY_BUSINESS_PERMIT}
            fileType={BusinessFileNames.BARANGAY_BUSINESS_PERMIT}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.BARANGAY_BUSINESS_PERMIT_BACK}
            fileType={BusinessFileNames.BARANGAY_BUSINESS_PERMIT_BACK}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.BARANGAY_BUSINESS_PERMIT_SIGNATURE}
            fileType={BusinessFileNames.BARANGAY_BUSINESS_PERMIT_SIGNATURE}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.STORE_PHOTO_WITH_STORE_NAME}
            fileType={BusinessFileNames.STORE_PHOTO_WITH_STORE_NAME}
          />
        </>
      );
    } else if (businessTypeId === BusinessTypes.SOLE_PROPRIETOR) {
      return (
        <>
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY_BACK}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY_BACK}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY_SIGNATURE}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY_SIGNATURE}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.MAYORS_OR_BUSINESS_PERMIT}
            fileType={BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.MAYORS_OR_BUSINESS_PERMIT_BACK}
            fileType={BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_BACK}
          />
          <ResponsiveCol
            label={
              BusinessFileNamesReadable.MAYORS_OR_BUSINESS_PERMIT_SIGNATURE
            }
            fileType={BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_SIGNATURE}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.BIR_CERTIFICATE_2303}
            fileType={BusinessFileNames.BIR_CERTIFICATE_2303}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.BIR_CERTIFICATE_2303_BACK}
            fileType={BusinessFileNames.BIR_CERTIFICATE_2303_BACK}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.BIR_CERTIFICATE_2303_SIGNATURE}
            fileType={BusinessFileNames.BIR_CERTIFICATE_2303_SIGNATURE}
          />
        </>
      );
    } else if (
      businessTypeId === BusinessTypes.PARTNERSHIP ||
      businessTypeId === BusinessTypes.CORPORATION
    ) {
      return (
        <>
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY_BACK}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY_BACK}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.VALID_IDS_OF_SIGNATORY_SIGNATURE}
            fileType={BusinessFileNames.VALID_IDS_OF_SIGNATORY_SIGNATURE}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.MAYORS_OR_BUSINESS_PERMIT}
            fileType={BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.MAYORS_OR_BUSINESS_PERMIT_BACK}
            fileType={BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_BACK}
          />
          <ResponsiveCol
            label={
              BusinessFileNamesReadable.MAYORS_OR_BUSINESS_PERMIT_SIGNATURE
            }
            fileType={BusinessFileNames.MAYORS_OR_BUSINESS_PERMIT_SIGNATURE}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.BIR_CERTIFICATE_2303}
            fileType={BusinessFileNames.BIR_CERTIFICATE_2303}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.BIR_CERTIFICATE_2303_BACK}
            fileType={BusinessFileNames.BIR_CERTIFICATE_2303_BACK}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.BIR_CERTIFICATE_2303_SIGNATURE}
            fileType={BusinessFileNames.BIR_CERTIFICATE_2303_SIGNATURE}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.SEC_CERTIFICATE}
            fileType={BusinessFileNames.SEC_CERTIFICATE}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.SEC_CERTIFICATE_BACK}
            fileType={BusinessFileNames.SEC_CERTIFICATE_BACK}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.SEC_CERTIFICATE_SIGNATURE}
            fileType={BusinessFileNames.SEC_CERTIFICATE_SIGNATURE}
          />

          <ResponsiveCol
            label={BusinessFileNamesReadable.AOI_AND_BY_LAWS}
            fileType={BusinessFileNames.AOI_AND_BY_LAWS}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.AOI_AND_BY_LAWS_BACK}
            fileType={BusinessFileNames.AOI_AND_BY_LAWS_BACK}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.AOI_AND_BY_LAWS_SIGNATURE}
            fileType={BusinessFileNames.AOI_AND_BY_LAWS_SIGNATURE}
          />

          {businessTypeId === BusinessTypes.CORPORATION && (
            <>
              <ResponsiveCol
                label={BusinessFileNamesReadable.GIS}
                fileType={BusinessFileNames.GIS}
              />
              <ResponsiveCol
                label={BusinessFileNamesReadable.GIS_BACK}
                fileType={BusinessFileNames.GIS_BACK}
              />
              <ResponsiveCol
                label={BusinessFileNamesReadable.GIS_SIGNATURE}
                fileType={BusinessFileNames.GIS_SIGNATURE}
              />
            </>
          )}
          <ResponsiveCol
            label={BusinessFileNamesReadable.SECRETARYS_CERTIFICATE}
            fileType={BusinessFileNames.SECRETARYS_CERTIFICATE}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.SECRETARYS_CERTIFICATE_BACK}
            fileType={BusinessFileNames.SECRETARYS_CERTIFICATE_BACK}
          />
          <ResponsiveCol
            label={BusinessFileNamesReadable.SECRETARYS_CERTIFICATE_SIGNATURE}
            fileType={BusinessFileNames.SECRETARYS_CERTIFICATE_SIGNATURE}
          />
        </>
      );
    }
  };

  const onError = (e: any) => {};
  return (
    <>
      <Modal
        padding='sm'
        opened={opened}
        shadow='0'
        onClose={() => {
          setOpened(false);
          setRetrievedFile((prev) => {
            return {
              ...prev,
              fileUrl: null,
            };
          });
        }}
        size='100%'
        sx={{ backgroundColor: 'transparent', height: '100%' }}
        styles={{
          inner: { padding: 0 },
          modal: { padding: 0, backgroundColor: 'transparent' },
          close: { padding: 0, backgroundColor: 'white' },
        }}
      >
        <Container
          fluid
          style={{
            padding: 0,
            margin: 0,
            height: '90vh',
            width: '100%',
            overflow: 'auto',
          }}
        >
          {!loading && retrievedFile.fileUrl !== null ? (
            <DocViewer
              documents={[
                {
                  uri: retrievedFile.fileUrl,
                  fileType: retrievedFile.fileData.fileType,
                  fileName: retrievedFile.fileData.fileName,
                },
              ]}
              pluginRenderers={DocViewerRenderers}
            />
          ) : (
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Loader />
            </div>
          )}
        </Container>
      </Modal>

      <Group position='apart'>
        <XInfoTabTitle label='Uploaded Documents' />
        {auth?.role === Roles.ADMINISTRATOR && (
          <Box style={{ textAlign: 'right' }}>
            <Button
              onClick={downloadFiles}
              leftIcon={<Download size='18' />}
              loading={isRetrievingFiles}
            >
              Download Files
            </Button>
          </Box>
        )}
      </Group>

      <Space h='xl' />
      <Grid>{filesToDisplay()}</Grid>
    </>
  );
}

export default MerchantDocuments;
